import { useState } from 'react'
import { Link, Navigate, useNavigate, useSearchParams } from 'react-router-dom'
import { useAuth } from '@/contexts/AuthContext'
import { Button } from '@/components/ui/button'
import { Eye, EyeOff, Mail, Lock, ArrowLeft, ShieldAlert, Sparkles, CheckCircle } from 'lucide-react'
import { SignIn } from '@clerk/clerk-react'
import { DEMO_AUTH_ENABLED } from '@/lib/env'

export default function LoginPage() {
  const { signIn, signInWithGoogle, signInWithGithub, signInWithMagicLink, isSignedIn } = useAuth()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const requestedRedirect = searchParams.get('redirect_url') || '/dashboard'
  const redirectUrl = requestedRedirect.startsWith('/') ? requestedRedirect : '/dashboard'

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [isGoogleLoading, setIsGoogleLoading] = useState(false)
  const [isGithubLoading, setIsGithubLoading] = useState(false)
  const [isMagicLinkLoading, setIsMagicLinkLoading] = useState(false)
  const [showMagicLinkForm, setShowMagicLinkForm] = useState(false)
  const [magicLinkEmail, setMagicLinkEmail] = useState('')
  const [magicLinkSent, setMagicLinkSent] = useState(false)
  const [error, setError] = useState('')

  if (!DEMO_AUTH_ENABLED) {
    return (
      <div className="min-h-screen bg-gradient-mesh flex items-center justify-center p-4">
        <SignIn
          routing="path"
          path="/login"
          signUpUrl="/signup"
          forceRedirectUrl={redirectUrl}
        />
      </div>
    )
  }

  // If already signed in, redirect
  if (isSignedIn) {
    return <Navigate to={redirectUrl} replace />
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setIsLoading(true)

    try {
      if (!email || !password) {
        throw new Error('Please fill in all fields')
      }

      await signIn(email, password)
      navigate(redirectUrl, { replace: true })
    } catch (err) {
      if (err instanceof Error && err.message === 'ACCOUNT_SUSPENDED') {
        setError('Your account has been suspended. Please contact support for assistance.')
      } else {
        setError(err instanceof Error ? err.message : 'Failed to sign in')
      }
    } finally {
      setIsLoading(false)
    }
  }

  const handleGoogleSignIn = async () => {
    setError('')
    setIsGoogleLoading(true)

    try {
      await signInWithGoogle()
      navigate(redirectUrl, { replace: true })
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to sign in with Google')
    } finally {
      setIsGoogleLoading(false)
    }
  }

  const handleGithubSignIn = async () => {
    setError('')
    setIsGithubLoading(true)

    try {
      await signInWithGithub()
      navigate(redirectUrl, { replace: true })
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to sign in with GitHub')
    } finally {
      setIsGithubLoading(false)
    }
  }

  const handleMagicLinkSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setIsMagicLinkLoading(true)

    try {
      if (!magicLinkEmail) {
        throw new Error('Please enter your email address')
      }

      await signInWithMagicLink(magicLinkEmail)
      setMagicLinkSent(true)
      // In demo mode, auto-login happens, so redirect after a brief delay
      setTimeout(() => {
        navigate(redirectUrl, { replace: true })
      }, 1500)
    } catch (err) {
      if (err instanceof Error && err.message === 'ACCOUNT_SUSPENDED') {
        setError('Your account has been suspended. Please contact support for assistance.')
      } else {
        setError(err instanceof Error ? err.message : 'Failed to send magic link')
      }
    } finally {
      setIsMagicLinkLoading(false)
    }
  }

  return (
    <div className="min-h-screen bg-gradient-mesh flex flex-col">
      {/* Header */}
      <header className="p-4">
        <Link to="/" className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground transition-colors">
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to home
        </Link>
      </header>

      {/* Main Content */}
      <main className="flex-1 flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          {/* Logo */}
          <div className="text-center mb-8">
            <Link to="/" className="inline-block">
              <h1 className="text-3xl font-bold text-primary">Photodrop</h1>
            </Link>
            <p className="text-muted-foreground mt-2">Welcome back! Sign in to your account.</p>
          </div>

          {/* Login Form */}
          <div className="bg-card text-card-foreground rounded-xl shadow-lg border p-8">
            {/* Error Message */}
            {error && (
              <div className="bg-destructive/10 border border-destructive/20 text-foreground px-4 py-3 rounded-lg text-sm mb-6">
                {error.includes('suspended') ? (
                  <div className="flex items-start gap-2">
                    <ShieldAlert className="h-5 w-5 flex-shrink-0 mt-0.5 text-destructive" />
                    <div>
                      <p className="font-medium text-destructive">Account Suspended</p>
                      <p className="text-destructive mt-1">{error}</p>
                    </div>
                  </div>
                ) : (
                  error
                )}
              </div>
            )}

            {/* Magic Link Success Message */}
            {magicLinkSent && (
              <div className="bg-secondary/10 border border-secondary/20 text-foreground px-4 py-4 rounded-lg mb-6">
                <div className="flex items-center gap-3">
                  <CheckCircle className="h-6 w-6 flex-shrink-0 text-secondary" />
                  <div>
                    <p className="font-medium">Check your email!</p>
                    <p className="text-muted-foreground text-sm mt-1">
                      We sent a sign-in link to {magicLinkEmail}. Click the link to sign in.
                    </p>
                    <p className="text-muted-foreground text-xs mt-2 italic">
                      (Demo mode: Auto-signing in...)
                    </p>
                  </div>
                </div>
              </div>
            )}

            {!showMagicLinkForm ? (
              // Password Sign In Form
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Email Field */}
                <div className="space-y-2">
                  <label htmlFor="email" className="block text-sm font-medium text-foreground">
                    Email address
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                    <input
                      id="email"
                      name="email"
                      type="email"
                      autoComplete="email"
                      required
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="w-full pl-10 pr-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary outline-none transition-all"
                      placeholder="you@example.com"
                    />
                  </div>
                </div>

                {/* Password Field */}
                <div className="space-y-2">
                  <label htmlFor="password" className="block text-sm font-medium text-foreground">
                    Password
                  </label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                    <input
                      id="password"
                      name="password"
                      type={showPassword ? 'text' : 'password'}
                      autoComplete="current-password"
                      required
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="w-full pl-10 pr-12 py-3 border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary outline-none transition-all"
                      placeholder="Enter your password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                    >
                      {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                    </button>
                  </div>
                </div>

                {/* Links Row */}
                <div className="flex justify-between items-center">
                  <button
                    type="button"
                    onClick={() => setShowMagicLinkForm(true)}
                    className="text-sm text-primary hover:underline flex items-center gap-1"
                  >
                    <Sparkles className="h-4 w-4" />
                    Sign in with email link
                  </button>
                  <Link to="/forgot-password" className="text-sm text-primary hover:underline">
                    Forgot password?
                  </Link>
                </div>

                {/* Submit Button */}
                <Button
                  type="submit"
                  className="w-full py-3 text-base"
                  disabled={isLoading}
                >
                  {isLoading ? 'Signing in...' : 'Sign in'}
                </Button>
              </form>
            ) : (
              // Magic Link Form
              <form onSubmit={handleMagicLinkSubmit} className="space-y-6">
                <div className="text-center mb-4">
                  <Sparkles className="h-10 w-10 text-primary mx-auto mb-2" />
                  <h2 className="text-lg font-semibold">Sign in with email link</h2>
                  <p className="text-sm text-muted-foreground mt-1">
                    We'll send you a magic link to sign in instantly
                  </p>
                </div>

                {/* Email Field */}
                <div className="space-y-2">
                  <label htmlFor="magic-email" className="block text-sm font-medium text-foreground">
                    Email address
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                    <input
                      id="magic-email"
                      name="magic-email"
                      type="email"
                      autoComplete="email"
                      required
                      value={magicLinkEmail}
                      onChange={(e) => setMagicLinkEmail(e.target.value)}
                      className="w-full pl-10 pr-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary outline-none transition-all"
                      placeholder="you@example.com"
                    />
                  </div>
                </div>

                {/* Submit Button */}
                <Button
                  type="submit"
                  className="w-full py-3 text-base"
                  disabled={isMagicLinkLoading || magicLinkSent}
                >
                  {isMagicLinkLoading ? 'Sending link...' : magicLinkSent ? 'Link sent!' : 'Send magic link'}
                </Button>

                {/* Back to password sign in */}
                <button
                  type="button"
                  onClick={() => {
                    setShowMagicLinkForm(false)
                    setMagicLinkSent(false)
                    setError('')
                  }}
                  className="w-full text-sm text-muted-foreground hover:text-foreground transition-colors"
                >
                  ← Back to password sign in
                </button>
              </form>
            )}

            {/* Divider */}
            <div className="relative my-6">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-card text-muted-foreground">Or continue with</span>
              </div>
            </div>

            {/* Social Login Buttons */}
            <div className="grid grid-cols-2 gap-3">
              <Button
                variant="outline"
                type="button"
                className="py-3"
                onClick={handleGoogleSignIn}
                disabled={isGoogleLoading || isGithubLoading || isLoading}
              >
                {isGoogleLoading ? (
                  <svg className="mr-2 h-5 w-5 animate-spin" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                ) : (
                  <svg className="mr-2 h-5 w-5" viewBox="0 0 24 24">
                    <path
                      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                      fill="#4285F4"
                    />
                    <path
                      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                      fill="#34A853"
                    />
                    <path
                      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                      fill="#FBBC05"
                    />
                    <path
                      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                      fill="#EA4335"
                    />
                  </svg>
                )}
                {isGoogleLoading ? 'Signing in...' : 'Google'}
              </Button>
              <Button
                variant="outline"
                type="button"
                className="py-3"
                onClick={handleGithubSignIn}
                disabled={isGoogleLoading || isGithubLoading || isLoading}
              >
                {isGithubLoading ? (
                  <svg className="mr-2 h-5 w-5 animate-spin" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                ) : (
                  <svg className="mr-2 h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z"/>
                  </svg>
                )}
                {isGithubLoading ? 'Signing in...' : 'GitHub'}
              </Button>
            </div>
          </div>

          {/* Sign Up Link */}
          <p className="text-center text-sm text-muted-foreground mt-6">
            Don't have an account?{' '}
            <Link to="/signup" className="text-primary font-medium hover:underline">
              Sign up for free
            </Link>
          </p>
        </div>
      </main>
    </div>
  )
}
